import {
  FeasibilityQuota,
  FeasibilityQuotaGroup,
  FeasibilityQuotaRow,
} from '@ps/survey-utils';
import { FeasibilityAnalysis } from './feasibility-analysis';
import { formatFeasibilityCondition } from './feasibility-condition.formatter';

/**
 * quotaSet x FeasibilityAnalysis -> FeasibilityQuotaGroup[]
 */
export class FeasibilityQuotaMapper {
  constructor(private readonly _analysis: FeasibilityAnalysis) {}

  public map(
    quotaSet: ReadonlyMap<number | string, readonly FeasibilityQuota[]>,
  ): FeasibilityQuotaGroup[] {
    return [...quotaSet.entries()].map(([key, rows]) =>
      this._mapGroup(key, rows),
    );
  }

  private _mapGroup(
    key: number | string,
    rows: readonly FeasibilityQuota[],
  ): FeasibilityQuotaGroup {
    const firstRow = rows[0];
    const qualificationCode = firstRow?.qualificationCode ?? 0;

    return {
      name: firstRow?.qualificationName ?? '',
      qualificationCode,
      quotas: rows.map((row) => this._mapQuota(key, row)),
      hasPopulationData: this._analysis.hasPopulationData(qualificationCode),
    };
  }

  private _mapQuota(
    groupKey: number | string,
    row: FeasibilityQuota,
  ): FeasibilityQuotaRow {
    const isUnknown = this._analysis.isUnknown(row.qualificationCode, row);
    const feasible = this._analysis.count(row.buyerQuotaId);

    return {
      buyerQuotaId: row.buyerQuotaId,
      groupKey: String(groupKey),
      label: formatFeasibilityCondition(row, this._analysis.currencySymbol),
      desired: row.requiredCount,
      estimate: isUnknown
        ? { kind: 'unknown' }
        : {
            kind: 'estimated',
            feasible,
            difference: feasible - row.requiredCount,
            isFeasible: feasible >= row.requiredCount,
          },
      isDifficult: this._analysis.isDifficult(row.buyerQuotaId),
    };
  }
}
